'use client';

import { forwardRef } from "react";
import { Box, Theme } from "@mui/material";
import { BoxProps, SystemStyleObject } from "@mui/system";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import styles from "./markdown.module.css";

interface TypographyOverrides {
    paragraph?: 'body1' | 'body2' | 'subtitle1' | 'subtitle2';
}

export type MarkdownProps = {
    value?: string;
    typographyOverrides?: TypographyOverrides;
    slots?: {
        root?: BoxProps;
    };
};

const sanitizeSchema = {
    ...defaultSchema,
    tagNames: [...(defaultSchema.tagNames ?? []), 'span'],
    attributes: {
        ...(defaultSchema.attributes ?? {}),
        span: [['className', 'md-text-small', 'md-text-normal', 'md-text-large', 'md-text-xlarge']],
    },
};

export const Markdown = forwardRef<HTMLDivElement, MarkdownProps>(({
    value = '',
    typographyOverrides,
    slots,
}, ref) => (
    <Box
        ref={ref}
        {...slots?.root}
        className={['markdown-root', styles.markdownRoot, slots?.root?.className].filter(Boolean).join(' ')}
        sx={[
            (theme) => ({
                position: 'relative',
                ...markdownRootSx(theme, typographyOverrides),
            }),
            ...(slots?.root?.sx
                ? (Array.isArray(slots.root.sx) ? slots.root.sx : [slots.root.sx])
                : []),
        ]}
    >
        <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkBreaks]}
            rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
        >
            {value}
        </ReactMarkdown>
    </Box>
));

Markdown.displayName = 'Markdown';

export const markdownRootSx = (theme: Theme, typographyOverrides?: TypographyOverrides): SystemStyleObject<Theme> => ({
    display: 'flex',
    flex: '1 1 0',
    flexDirection: 'column',
    minWidth: 0,
    mx: '1px',
    '& h1': { ...(theme.typography?.h1 ?? {}), margin: 0, marginBottom: `${theme.spacing(4)} !important` },
    '& h2': { ...(theme.typography?.h2 ?? {}), margin: 0, marginBottom: `${theme.spacing(3.75)} !important` },
    '& h3': { ...(theme.typography?.h3 ?? {}), margin: 0, marginBottom: `${theme.spacing(3.5)} !important` },
    '& h4': { ...(theme.typography?.h4 ?? {}), margin: 0, marginBottom: `${theme.spacing(3.25)} !important` },
    '& h5': { ...(theme.typography?.h5 ?? {}), margin: 0, marginBottom: `${theme.spacing(3)} !important` },
    '& h6': { ...(theme.typography?.h6 ?? {}), margin: 0, marginBottom: `${theme.spacing(2.75)} !important` },
    '& p': {
        ...(typographyOverrides?.paragraph ? theme.typography[typographyOverrides.paragraph] : theme.typography?.body1 ?? {}),
        margin: 0,
        marginBottom: theme.spacing(2),
    },
    '& p:last-child': { marginBottom: 0 },
    '& a': {
        color: theme.palette.mode === 'light' ? theme.palette.primary.dark : theme.palette.primary.main,
        textDecoration: 'underline',
        '&:hover': {
            color: theme.palette.mode === 'light' ? theme.palette.primary.dark : theme.palette.primary.light,
            textDecoration: 'underline',
        },
    },
    '& ul': { listStyle: 'disc', paddingLeft: theme.spacing(2), marginTop: theme.spacing(1), marginBottom: theme.spacing(2) },
    '& ol': { listStyle: 'decimal', paddingLeft: theme.spacing(2), marginTop: theme.spacing(1), marginBottom: theme.spacing(2) },
    '& li': { marginBottom: theme.spacing(0.25) },
    '& blockquote': {
        margin: 0,
        marginBottom: theme.spacing(2),
        paddingLeft: theme.spacing(2),
        borderLeft: `4px solid ${theme.palette.divider}`,
        color: theme.palette.text.secondary,
        fontStyle: 'italic',
    },
    '& :not(pre) > code': {
        fontFamily: 'var(--font-geist-mono), ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
        backgroundColor: theme.palette.action.hover,
        borderRadius: theme.shape.borderRadius,
        padding: '0 4px',
        fontSize: '0.85em',
    },
    '& pre': {
        ...(theme.typography?.body2 ?? {}),
        fontFamily: 'var(--font-geist-mono), ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
        backgroundColor: theme.palette.action.hover,
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(2),
        overflow: 'auto',
        margin: 0,
        marginBottom: theme.spacing(2),
    },
    '& pre > code': { background: 'transparent', padding: 0 },
    '& hr': { border: 0, borderTop: `1px solid ${theme.palette.divider}`, margin: `${theme.spacing(3)} 0` },
    '& img': { maxWidth: '100%', height: 'auto' },
});
